import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { domAnimation, LazyMotion } from 'motion/react';
import * as z from 'zod';

import { OAuthProvider } from '#modules/user/features/auth/constants/providers';
import { useEnsureTimeZoneOffset } from '#modules/user/features/profile/hooks/useEnsureTimeZoneOffset';
import { usePageView } from '#features/analytics/hooks/usePageView';
import { useTaggedLanding } from '#features/analytics/hooks/useTaggedLanding';
import { CallOverlay } from '#features/calls/components/CallOverlay';
import { useMutedChatsMirror } from '#features/chats/hooks/useMutedChatsMirror';
import { ErrorNotFound } from '#features/error/components/ErrorNotFound';
import { parseLocaleParam } from '#features/i18n/utils/localeParam';
import { MainLayout } from '#features/layout/components/MainLayout';
import { useIdleReporter } from '#features/presence/hooks/useIdleReporter';
import { usePresenceSync } from '#features/presence/hooks/usePresence';
import { useForegroundRefresh } from '#features/realtime/hooks/useForegroundRefresh';
import { useRealtimeNotifications } from '#features/realtime/hooks/useRealtimeNotifications';
import { useRealtimeSubscription } from '#features/realtime/hooks/useRealtimeSubscription';
import { SESSION_BROADCAST_TOPICS } from '#features/realtime/topics';
import { SessionRoomHost } from '#features/session-room';
import { VoiceRoomProvider } from '#features/voice/components/VoiceRoomProvider';

// Make sure to return the specific export containing the feature bundle.
// const loadFeatures = () =>
//   import('#features/ui/constants/motionFeatures').then((res) => res.domAnimation);

// Схема висит на layout'е, под которым лежит весь сайт, а оба параметра приходят снаружи —
// из OAuth-возвратов и чужих ссылок. Разбираем их мягко: без `.catch` неизвестный провайдер
// (`?provider=xbox`) или задвоенный параметр (`?url=a&url=b`) ронял SSR всей страницы в 500
// вместо того, чтобы просто вычиститься из адреса.
const layoutSchema = z.object({
  provider: z.enum(OAuthProvider).optional().catch(undefined),
  url: z.string().optional().catch(undefined),
});

const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <LazyMotion features={domAnimation}>
      <MainLayout>
        {/* Голос сессии: соединение и элементы <audio> живут здесь, выше роутера, поэтому переход
            между страницами разговор не прерывает. Провайдер обнимает и страницы: ярус голоса
            стоит не только в комнате, но и над лентой в треде чата сессии – то есть внутри
            `<Outlet/>`, – а действия микрофона и устройств работают только у владельца
            соединения.

            Стоит именно здесь, а не рядом с `<Outlet/>`: у лейаута две ветки, обычная и «страницы
            нет», и обе рисует эта обёртка. Живи провайдер во второй ветке отдельно, «выше по
            дереву его нет» снова стало бы вопросом того, какую ветку сегодня выбрал роутер. */}
        <VoiceRoomProvider>{children}</VoiceRoomProvider>
      </MainLayout>
    </LazyMotion>
  );
};

export const Route = createFileRoute('/{-$locale}/_layout')({
  // Тот же разбор, что у маршрута языка, - иначе его разбор здесь теряется (см. parseLocaleParam).
  params: { parse: parseLocaleParam },
  component: LayoutComponent,
  notFoundComponent: () => (
    <Providers>
      <ErrorNotFound />
    </Providers>
  ),
  validateSearch: zodValidator(layoutSchema),
});

function LayoutComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // The fixture owns its query cache and application shell. Live layout effects would
  // otherwise overwrite the selected identity and session role during visual QA.
  if (
    import.meta.env.DEV &&
    /^(?:\/[^/]+)?\/preview\/(?:session-compare|session-live|session-invite|apple-auth|notifications|match-alerts|finder-filters)\/?$/.test(
      pathname
    )
  ) {
    return <Outlet />;
  }
  // Изолированная проверка голоса не подключает LiveKit и не занимает микрофон.
  if (import.meta.env.DEV && /^(?:\/[^/]+)?\/preview\/session-room\/?$/.test(pathname)) {
    return (
      <LazyMotion features={domAnimation}>
        <MainLayout>
          <Outlet />
        </MainLayout>
      </LazyMotion>
    );
  }
  return <LiveLayoutComponent />;
}

function LiveLayoutComponent() {
  usePageView();
  useTaggedLanding();
  useEnsureTimeZoneOffset();
  useRealtimeNotifications();
  // Заглушённые переписки одним списком сразу после входа: всплывашка спрашивает про них стор, а
  // на свежем устройстве он пуст, пока человек не заглянет в чаты, - и первая всплывашка из
  // заглушённой переписки проскакивала.
  useMutedChatsMirror();
  // Страховка сокета: вернулся на экран - бейджи Chats и колокольчика перечитываются, даже если
  // сокет всё это время был открыт, но молчал.
  useForegroundRefresh();
  // Один опрос присутствия на всё приложение: экраны только регистрируют, на кого
  // смотрят, а спрашивает сервер отсюда — иначе список чатов, открытый чат и лента
  // тянули бы статус каждый сам и показывали снимки разного возраста.
  usePresenceSync();
  // Сообщаем серверу, за экраном человек или отошёл: по самому соединению этого не видно,
  // а от этого зависят и жёлтый статус, и то, придёт ли ему push о сообщении.
  useIdleReporter();
  // Subscribe to broadcast session/post topics once for the whole app, so every
  // page receives live updates (toasts + cache invalidation). Kept here at the
  // always-mounted layout root rather than per-page, so navigating between pages
  // doesn't unsubscribe topics out from under other pages.
  useRealtimeSubscription(SESSION_BROADCAST_TOPICS);

  return (
    <Providers>
      <Outlet />
      {/* Global 1:1 call UI (incoming ring / in-call window) — lives next to the WS
          hook so call events always have a mounted consumer. */}
      <CallOverlay />
      {/* Комната сессии: «я сейчас в сессии» одним предметом – состав, голос и переписка.
          Плавающего окна разговора рядом больше нет: голос стал ярусом комнаты, и второй
          жилец угла публиковал бы ту же `--voice-bar-space`, стирая резерв живого соседа при
          своём размонтировании. */}
      <SessionRoomHost />
    </Providers>
  );
}
